import {
  Project,
  ProjectCreateInput,
  ProjectChatInput,
  ProjectChatResponse,
  ProjectGradeInput,
  ProjectGradeResponse,
  ProjectMessage,
} from '../types/project';
import { API_BASE_URL } from '../utils/chatConstants';
import { withCsrfHeader } from '../utils/authSession';

/**
 * ProjectService - Backend API-based implementation.
 *
 * All project data is persisted in the backend and synced with Tutor Engine.
 */

const API_BASE = API_BASE_URL;

async function getAuthHeaders(includeJsonContentType: boolean = true): Promise<HeadersInit> {
  const headers: HeadersInit = {
    ...(includeJsonContentType ? { 'Content-Type': 'application/json' } : {}),
  };
  return withCsrfHeader(headers);
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error: ${response.status} - ${errorText}`);
  }
  return response.json();
}

type ProjectMediaUploadResponse = {
  message: ProjectMessage;
  answer?: string | null;
  project?: Project;
};

/**
 * Get current user ID from auth state.
 */
function getCurrentUserId(): string {
  // Try to get from localStorage user data
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      return user.id || user.userId || '';
    } catch {
      // fallback
    }
  }
  // Fallback: use a stored user_id
  return localStorage.getItem('user_id') || '';
}

export const projectService = {
  /**
   * List all projects for the current user.
   */
  async list(): Promise<Project[]> {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('projectService.list: No user ID available');
      return [];
    }

    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects?student_id=${encodeURIComponent(userId)}`,
      { method: 'GET', headers, credentials: 'include' }
    );
    
    const data = await handleResponse<{ projects: Project[] }>(response);
    return data.projects || [];
  },

  /**
   * Get a project by ID.
   */
  async getById(projectId: string): Promise<Project | undefined> {
    if (!projectId) return undefined;

    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}`,
      { method: 'GET', headers, credentials: 'include' }
    );

    if (response.status === 404) {
      return undefined;
    }

    return handleResponse<Project>(response);
  },

  /**
   * Create a new project.
   */
  async create(input: ProjectCreateInput): Promise<Project> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('Cannot create project: No user ID available');
    }

    const headers = await getAuthHeaders();
    const body = {
      student_id: userId,
      subject_id: input.subject_id,
      grade_level: input.grade_level,
      form: input.form,
      title: input.title,
      project_brief: input.project_brief,
    };

    const response = await fetch(`${API_BASE}/projects/start`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });

    return handleResponse<Project>(response);
  },

  /**
   * Send a chat message in project coaching mode.
   */
  async chat(projectId: string, input: ProjectChatInput): Promise<ProjectChatResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/chat`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          message: input.message,
          allow_web_search: input.allow_web_search ?? true,
          thread_id: input.thread_id,
        }),
      }
    );

    return handleResponse<ProjectChatResponse>(response);
  },

  /**
   * Send a chat message with streaming response.
   */
  async chatStream(
    projectId: string,
    input: ProjectChatInput,
    onChunk?: (chunk: string, fullContent: string) => void
  ): Promise<ProjectChatResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/chat/stream`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          message: input.message,
          allow_web_search: input.allow_web_search ?? true,
          thread_id: input.thread_id,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    let lastProject: Project | null = null;
    let clarificationPayload: ProjectChatResponse['clarification'] | undefined;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const data = JSON.parse(jsonStr);
            
            if (data.type === 'chunk' && data.content) {
              fullContent += data.content;
              onChunk?.(data.content, fullContent);
            } else if (data.type === 'done') {
              if (data.project) {
                lastProject = data.project;
              }
              if (data.clarification && typeof data.clarification === 'object') {
                clarificationPayload = data.clarification as ProjectChatResponse['clarification'];
              }
              fullContent = data.answer || fullContent;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      // Handle any trailing data without a newline
      const tail = buffer.trim();
      if (tail.startsWith('data: ')) {
        const jsonStr = tail.slice(6).trim();
        if (jsonStr) {
          try {
            const data = JSON.parse(jsonStr);
            if (data.type === 'chunk' && data.content) {
              fullContent += data.content;
              onChunk?.(data.content, fullContent);
            } else if (data.type === 'done') {
              if (data.project) {
                lastProject = data.project;
              }
              if (data.clarification && typeof data.clarification === 'object') {
                clarificationPayload = data.clarification as ProjectChatResponse['clarification'];
              }
              fullContent = data.answer || fullContent;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return {
      answer: fullContent,
      project: lastProject || {} as Project,
      clarification: clarificationPayload,
    };
  },

  /**
   * Grade a project submission.
   */
  async grade(projectId: string, input: ProjectGradeInput): Promise<ProjectGradeResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/grade`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          submission_text: input.submission_text,
          allow_web_search: input.allow_web_search ?? true,
        }),
      }
    );

    return handleResponse<ProjectGradeResponse>(response);
  },

  /**
   * Delete a project.
   */
  async delete(projectId: string): Promise<{ success: boolean; message: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}`,
      {
        method: 'DELETE',
        headers,
        credentials: 'include',
      }
    );

    return handleResponse<{ success: boolean; message: string }>(response);
  },

  /**
   * Get chat history for a project.
   */
  async getMessages(projectId: string, limit: number = 100): Promise<ProjectMessage[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/messages?limit=${limit}`,
      {
        method: 'GET',
        headers,
        credentials: 'include',
      }
    );

    const data = await handleResponse<{ messages: ProjectMessage[] }>(response);
    return data.messages || [];
  },

  /**
   * Upload a media/file attachment in project chat.
   */
  async uploadMedia(
    projectId: string,
    file: File,
    fileType: 'AUDIO' | 'IMAGE' | 'VIDEO' | 'FILE' | 'DOCUMENT',
    content?: string,
    duration?: number,
    allowWebSearch: boolean = true
  ): Promise<ProjectMediaUploadResponse> {
    const headers = await getAuthHeaders(false);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    formData.append('allow_web_search', String(Boolean(allowWebSearch)));
    if (typeof content === 'string' && content.trim()) {
      formData.append('content', content.trim());
    }
    if (typeof duration === 'number' && Number.isFinite(duration)) {
      formData.append('duration', String(Math.max(0, Math.floor(duration))));
    }

    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/upload/media`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      }
    );
    return handleResponse<ProjectMediaUploadResponse>(response);
  },

  /**
   * Resolve media_id into an object URL.
   */
  async getMediaObjectUrl(mediaId: string): Promise<string> {
    const response = await fetch(
      `${API_BASE}/chat/media/${encodeURIComponent(mediaId)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Failed to fetch media (${response.status}): ${errText}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Delete a single message from a project chat.
   */
  async deleteMessage(messageId: string): Promise<{ message: string; message_id: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/projects/messages/${encodeURIComponent(messageId)}`,
      {
        method: 'DELETE',
        headers,
        credentials: 'include',
      }
    );

    return handleResponse<{ message: string; message_id: string }>(response);
  },
};
